import { useEffect } from 'react'
import { useCliente } from '@/lib/graphql/hooks'
import type { Cliente, Contratto } from '@/lib/graphql/types'
import { pickActiveContract, useAuthStore } from '../authStore'

export type ActiveContractState = {
  cliente: Cliente | null
  contratti: Contratto[]
  monitoraggio: Contratto[]
  activeContract: Contratto | null
  isLoading: boolean
  error: Error | null
}

/**
 * Hook orchestratore: data il `codiceCliente` Cognito del custom attribute,
 * carica i contratti via AppSync, calcola il contratto attivo (il primo o
 * quello scelto dall'utente) ed espone lo stato pronto al consumo.
 *
 * Replica `Route.setCurrentUserData` del mobile (caricare cliente + contratti
 * subito dopo login), ma in modo dichiarativo con TanStack Query.
 */
export function useActiveContract(): ActiveContractState {
  const user = useAuthStore((s) => s.user)
  const activeCode = useAuthStore((s) => s.activeContractCode)
  const setActiveContract = useAuthStore((s) => s.setActiveContract)

  const codiceCliente = user?.codiceCliente
  const query = useCliente(codiceCliente)

  const contratti = query.data?.contratti ?? []
  const monitoraggio = query.data?.monitoraggio ?? []
  const activeContract = pickActiveContract(contratti, monitoraggio, activeCode)

  // Auto-selezione: se l'utente non ha scelto nessun contratto ma ne ha uno
  // solo in totale (propri + monitorati), salviamo subito la preferenza.
  useEffect(() => {
    if (activeCode) return
    const all = [...contratti, ...monitoraggio]
    if (all.length === 1) {
      setActiveContract(all[0].codice_contratto)
    }
  }, [activeCode, contratti, monitoraggio, setActiveContract])

  return {
    cliente: activeContract?.cliente ?? null,
    contratti,
    monitoraggio,
    activeContract,
    isLoading: query.isLoading,
    error: query.error,
  }
}
